import React, { useState, useEffect } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  Users, 
  Clock, 
  Package, 
  DollarSign,
  Target,
  ArrowUp,
  ArrowDown,
  Minus,
  Plus,
  Trash2,
  BarChart3,
  Calendar,
  Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Performance() {
  const [periodo, setPeriodo] = useState('dia');
  const [performance, setPerformance] = useState(null);
  const [evolucao, setEvolucao] = useState([]);
  const [metas, setMetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMetaDialog, setShowMetaDialog] = useState(false);
  const [metaForm, setMetaForm] = useState({
    nome: '',
    tipo: 'diaria',
    categoria: '',
    valor_meta: 100
  });
  const [categorias, setCategorias] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [perfRes, evolRes, metasRes, catRes] = await Promise.all([
        axios.get(`${API}/analytics/performance/equipa?periodo=${periodo}`),
        axios.get(`${API}/analytics/performance/evolucao?dias=30`),
        axios.get(`${API}/metas`),
        axios.get(`${API}/categorias`)
      ]);
      
      setPerformance(perfRes.data);
      setEvolucao(evolRes.data);
      setMetas(metasRes.data);
      setCategorias(catRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados de performance');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeta = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/metas`, metaForm);
      toast.success('Meta criada com sucesso');
      setShowMetaDialog(false);
      setMetaForm({ nome: '', tipo: 'diaria', categoria: '', valor_meta: 100 });
      loadData();
    } catch (error) {
      toast.error('Erro ao criar meta');
    }
  };

  const handleDeleteMeta = async (metaId) => {
    if (!window.confirm('Eliminar esta meta?')) return;
    try {
      await axios.delete(`${API}/metas/${metaId}`);
      toast.success('Meta eliminada');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar meta');
    }
  };

  const getProgressoMeta = (meta) => {
    if (!performance) return 0;
    
    if (meta.categoria) {
      const catData = performance.por_categoria[meta.categoria];
      return catData ? Math.round((catData.pecas / meta.valor_meta) * 100) : 0;
    }
    
    return Math.round((performance.total_pecas / meta.valor_meta) * 100);
  };

  const getVariacaoColor = (valor) => {
    if (valor > 0) return 'text-green-600';
    if (valor < 0) return 'text-red-600';
    return 'text-stone-500';
  };

  const getVariacaoIcon = (valor) => {
    if (valor > 0) return ArrowUp;
    if (valor < 0) return ArrowDown;
    return Minus;
  };

  // Calcular variação em relação ao dia anterior
  const calcularVariacao = () => {
    if (evolucao.length < 2) return null;
    const hoje = evolucao[evolucao.length - 1];
    const ontem = evolucao[evolucao.length - 2];
    
    if (!hoje || !ontem) return null;
    
    const variacao = ((hoje.pecas - ontem.pecas) / ontem.pecas) * 100;
    return isFinite(variacao) ? variacao : 0;
  };

  const variacao = calcularVariacao();

  if (loading) {
    return (
      <GestorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </GestorLayout>
    );
  }

  return (
    <GestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">Performance</h1>
            <p className="text-stone-600">Análise de performance da equipa e metas de produção</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Seletor de período */}
            <div className="flex gap-2 bg-stone-100 p-1 rounded-full">
              {['dia', 'semana', 'mes'].map((p) => (
                <Button
                  key={p}
                  variant={periodo === p ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPeriodo(p)}
                  className="rounded-full capitalize"
                >
                  {p === 'dia' ? 'Hoje' : p === 'semana' ? 'Semana' : 'Mês'}
                </Button>
              ))}
            </div>
            <Button onClick={() => setShowMetaDialog(true)} className="rounded-full">
              <Target className="h-4 w-4 mr-2" />
              Nova Meta
            </Button>
          </div>
        </div>

        {/* KPIs Principais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-white border-blue-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium mb-1">Peças Produzidas</p>
                <p className="text-4xl font-bold text-blue-800">{performance?.total_pecas || 0}</p>
                {variacao !== null && (
                  <div className={`flex items-center gap-1 mt-2 text-sm ${getVariacaoColor(variacao)}`}>
                    {React.createElement(getVariacaoIcon(variacao), { className: "h-4 w-4" })}
                    <span>{Math.abs(variacao).toFixed(1)}% vs ontem</span>
                  </div>
                )}
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-white border-green-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium mb-1">Eficiência Média</p>
                <p className="text-4xl font-bold text-green-800">{performance?.eficiencia_media || 0}%</p>
                <p className="text-xs text-green-600 mt-2">
                  {performance?.eficiencia_media >= 100 ? '✓ Acima do esperado' : 'Em progresso'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Zap className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-white border-purple-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium mb-1">Tempo Produtivo</p>
                <p className="text-4xl font-bold text-purple-800">{performance?.total_tempo_horas || 0}h</p>
                <p className="text-xs text-purple-600 mt-2">
                  {performance?.operadores_ativos || 0} operadores ativos
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-amber-50 to-white border-amber-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium mb-1">Custo Operacional</p>
                <p className="text-4xl font-bold text-amber-800">€{performance?.custo_total || 0}</p>
                <p className="text-xs text-amber-600 mt-2">
                  €{performance?.custo_por_peca?.toFixed(3) || 0}/peça
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Metas e Performance por Categoria */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Metas */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-bold text-stone-900 flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Metas de Produção
              </h2>
            </div>

            {metas.length === 0 ? (
              <div className="text-center py-8">
                <Target className="h-12 w-12 mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500">Nenhuma meta definida</p>
                <Button 
                  variant="outline" 
                  className="mt-3"
                  onClick={() => setShowMetaDialog(true)}
                >
                  Criar meta
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {metas.map((meta) => {
                  const progresso = getProgressoMeta(meta);
                  const atingido = progresso >= 100;
                  
                  return (
                    <div key={meta.id} className={`p-4 rounded-lg border ${atingido ? 'bg-green-50 border-green-200' : 'bg-stone-50 border-stone-200'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-stone-900">{meta.nome}</p>
                          <p className="text-xs text-stone-500">
                            {meta.categoria || 'Geral'} • Meta {meta.tipo}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {atingido && <span className="text-green-600 text-xl">🎉</span>}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-stone-400 hover:text-red-500"
                            onClick={() => handleDeleteMeta(meta.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="w-full bg-stone-200 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full transition-all ${
                                atingido ? 'bg-green-500' : progresso > 70 ? 'bg-blue-500' : 'bg-amber-500'
                              }`}
                              style={{ width: `${Math.min(progresso, 100)}%` }}
                            />
                          </div>
                        </div>
                        <span className={`text-sm font-bold ${atingido ? 'text-green-600' : 'text-stone-700'}`}>
                          {progresso}%
                        </span>
                      </div>
                      
                      <p className="text-xs text-stone-500 mt-2">
                        {performance?.total_pecas || 0} / {meta.valor_meta} peças
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Performance por Categoria */}
          <Card className="p-6">
            <h2 className="text-xl font-heading font-bold text-stone-900 mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Performance por Categoria
            </h2>

            {Object.keys(performance?.por_categoria || {}).length === 0 ? (
              <div className="text-center py-8">
                <BarChart3 className="h-12 w-12 mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500">Sem dados de produção</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(performance.por_categoria).map(([categoria, dados]) => (
                  <div key={categoria} className="p-4 bg-stone-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-stone-800 capitalize">{categoria}</span>
                      <span className="text-lg font-bold text-primary">{dados.pecas} peças</span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-stone-500">Tempo</p>
                        <p className="font-medium">{Math.round(dados.tempo / 3600 * 10) / 10}h</p>
                      </div>
                      <div>
                        <p className="text-stone-500">Registos</p>
                        <p className="font-medium">{dados.registos}</p>
                      </div>
                      <div>
                        <p className="text-stone-500">Peças/Hora</p>
                        <p className="font-medium">
                          {dados.tempo > 0 ? Math.round(dados.pecas / (dados.tempo / 3600)) : 0}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Evolução da Produção */}
        <Card className="p-6">
          <h2 className="text-xl font-heading font-bold text-stone-900 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Evolução da Produção (Últimos 30 dias)
          </h2>

          {evolucao.length === 0 ? (
            <div className="text-center py-8">
              <TrendingUp className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">Sem dados de evolução</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex gap-2 min-w-max pb-4">
                {evolucao.slice(-14).map((dia, idx) => {
                  const maxPecas = Math.max(...evolucao.map(d => d.pecas));
                  const altura = maxPecas > 0 ? (dia.pecas / maxPecas) * 120 : 0;
                  
                  return (
                    <div key={dia.data} className="flex flex-col items-center">
                      <div 
                        className="w-12 bg-primary/20 rounded-t-lg flex items-end justify-center transition-all hover:bg-primary/30"
                        style={{ height: `${Math.max(altura, 4)}px` }}
                      >
                        <span className="text-xs font-medium text-primary mb-1">{dia.pecas}</span>
                      </div>
                      <div className="mt-2 text-center">
                        <p className="text-xs text-stone-500">
                          {new Date(dia.data).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                        </p>
                        <p className={`text-xs font-medium ${dia.eficiencia >= 100 ? 'text-green-600' : 'text-stone-600'}`}>
                          {dia.eficiencia}%
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>

        {/* Recursos Humanos */}
        <Card className="p-6">
          <h2 className="text-xl font-heading font-bold text-stone-900 mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Recursos Humanos
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-sm text-blue-600 mb-1">Operadores Ativos</p>
              <p className="text-3xl font-bold text-blue-800">{performance?.operadores_ativos || 0}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <p className="text-sm text-purple-600 mb-1">Horas-Homem</p>
              <p className="text-3xl font-bold text-purple-800">{performance?.total_tempo_horas || 0}h</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <p className="text-sm text-amber-600 mb-1">Custo RH Total</p>
              <p className="text-3xl font-bold text-amber-800">€{performance?.custo_total || 0}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-sm text-green-600 mb-1">Custo Médio/Hora</p>
              <p className="text-3xl font-bold text-green-800">€10.00</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Dialog Nova Meta */}
      <Dialog open={showMetaDialog} onOpenChange={setShowMetaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Nova Meta de Produção</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateMeta} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Nome da Meta</label>
              <Input
                value={metaForm.nome}
                onChange={(e) => setMetaForm({ ...metaForm, nome: e.target.value })}
                placeholder="ex: Meta diária de acabamentos"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Tipo</label>
                <select
                  value={metaForm.tipo}
                  onChange={(e) => setMetaForm({ ...metaForm, tipo: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                >
                  <option value="diaria">Diária</option>
                  <option value="semanal">Semanal</option>
                  <option value="mensal">Mensal</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Categoria</label>
                <select
                  value={metaForm.categoria}
                  onChange={(e) => setMetaForm({ ...metaForm, categoria: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                >
                  <option value="">Geral</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.nome}>{cat.nome}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Valor da Meta (peças)
              </label>
              <Input
                type="number"
                value={metaForm.valor_meta}
                onChange={(e) => setMetaForm({ ...metaForm, valor_meta: parseInt(e.target.value) || 0 })}
                min="1"
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowMetaDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1">
                Criar Meta
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </GestorLayout>
  );
}
